import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { DockviewReact, themeAbyss, type DockviewApi, type DockviewReadyEvent, type IDockviewHeaderActionsProps, type IDockviewPanelProps } from 'dockview-react'
import 'dockview-react/dist/styles/dockview.css'
import { create } from 'zustand'
import { Check, MoreHorizontal, Play, Plus, UserRound } from 'lucide-react'
import type { AgentSession } from '@one-deployment/api'
import { Menu } from '@/components/ui'
import { Button } from '@/components/shadcn/button'
import { ErrorState } from '@/components/status'
import { RoleGate } from '@/components/RoleGate'
import { previewLink } from '@/lib/box'
import { cn } from '@/lib/cn'
import { useMe } from '@/features/auth/useMe'
import { terminalMode } from '@/lib/can'
import { TerminalPane, type TermStatus } from '@/features/workspace/Terminal'
import { ChatPane } from '@/features/workspace/Chat'
import { FilePanel } from '@/features/workspace/FilePanel'
import { useRun } from './RunPanel'
import { McpButton } from '@/features/projects/Mcp'
import { ConversationBar } from '@/features/workspace/chat/Conversations'
import { useConversations } from '@/stores/conversations'
import { useProject } from '@/features/projects/data'

/**
 * A session's windows: the chat with Claude, its terminal, the files, the
 * app's preview and the dev server's terminal. A session opens on the chat
 * alone; the rest come from the + on any window's header. Each can be dragged anywhere, split, stacked or resized;
 * the arrangement is kept per browser and reused for every session.
 *
 * Every window has one header row: its tab on the left, its status and
 * actions on the right (dockview's group header actions).
 */
type Ctx = { session: AgentSession; project: string; run: boolean; openRun: () => void }
const SessionCtx = createContext<Ctx | null>(null)
const useSession = () => useContext(SessionCtx)!

/** What each window reports to its header: terminal status, preview reloads. */
const useDock = create<{ status: Record<string, TermStatus>; reload: number; setStatus: (id: string, s: TermStatus) => void; bump: () => void }>()((set) => ({
  status: {},
  reload: 0,
  setStatus: (id, s) => set((x) => ({ status: { ...x.status, [id]: s } })),
  bump: () => set((x) => ({ reload: x.reload + 1 })),
}))

/**
 * The Preview's "Sign in as" pick, per project and browser. The preview link
 * carries it; the app switches only when it differs from the last pick it saw,
 * so a sign-in or sign-out made inside the app is kept.
 */
const asKey = (project: string) => `trisof.preview.as.${project}`
const usePreviewAs = create<{ as: Record<string, string | null>; set: (project: string, email: string) => void }>()((set) => ({
  as: {},
  set: (project, email) => { localStorage.setItem(asKey(project), email); set((x) => ({ as: { ...x.as, [project]: email } })) },
}))
function useAs(project: string): string | null {
  const v = usePreviewAs((x) => x.as[project])
  return v === undefined ? localStorage.getItem(asKey(project)) : v
}

const LAYOUT_KEY = 'trisof.dock.v3'
const RUN_PANELS = ['preview', 'devserver'] as const

function useStatus(id: string) {
  const setStatus = useDock((s) => s.setStatus)
  return useCallback((s: TermStatus) => setStatus(id, s), [id, setStatus])
}
function ClaudeWindow(_: IDockviewPanelProps) {
  const { session, project } = useSession()
  return <ChatPane session={session} project={project} bare onStatus={useStatus('claude')} />
}
function Term({ id, run }: { id: string; run?: boolean }) {
  const { session, project } = useSession()
  return <TerminalPane session={session} project={project} run={run} bare onStatus={useStatus(id)} />
}
const TerminalWindow = (_: IDockviewPanelProps) => <Term id="terminal" />
const DevServerWindow = (_: IDockviewPanelProps) => <Term id="devserver" run />
function FilesWindow({ api }: IDockviewPanelProps) {
  const { session, project } = useSession()
  const mode = terminalMode(useMe().data, project)
  return <FilePanel sessionId={session.id} mode={mode} onHide={() => api.close()} />
}

function PreviewWindow(_: IDockviewPanelProps) {
  const { session, project } = useSession()
  const as = useAs(project)
  const reload = useDock((s) => s.reload)
  const [src, setSrc] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<unknown>(null)
  // The box reports a page that painted nothing; reload it, up to three times.
  const [tries, setTries] = useState(0)
  const [blank, setBlank] = useState(false)
  const [blocker, setBlocker] = useState(false)
  useEffect(() => { setTries(0); setBlank(false); setBlocker(false) }, [session.id, reload])
  const link = useCallback(() => previewLink(session.id).then((u) => (as ? `${u}&as=${encodeURIComponent(as)}` : u)), [session.id, as])
  useEffect(() => { setErr(null); setLoading(true); link().then(setSrc, setErr) }, [link, reload, tries])
  useEffect(() => {
    if (!src) return
    const from = new URL(src).origin
    const on = (e: MessageEvent) => {
      if (e.origin !== from || e.data?.trisof !== 'preview') return
      if (!e.data.blank) return setBlank(false)
      // An extension blocking the app's files: reloading cannot help, say so now.
      if (e.data.blocker) { setBlocker(true); return setBlank(true) }
      if (tries < 3) setTries((t) => t + 1)
      else setBlank(true)
    }
    window.addEventListener('message', on)
    return () => window.removeEventListener('message', on)
  }, [src, tries])

  return (
    <div className="relative size-full bg-bg">
      {err ? <div className="p-2"><ErrorState error={err} compact /></div> : null}
      {/* No top navigation: an app under development must not be able to
          replace the dashboard around it. */}
      {src && <iframe key={src} src={src} title="Preview" onLoad={() => setLoading(false)} className="size-full border-0 bg-white" sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads" />}
      {blank && !loading && (
        <div className="absolute inset-0 grid place-items-center bg-bg text-xs text-fg-subtle">
          <div className="flex flex-col items-center gap-3">
            {blocker
              ? <span className="max-w-sm text-center">A browser extension (an ad or tracker blocker) is blocking the app's files. Allow <b>{new URL(src!).hostname}</b> in it, then reload.</span>
              : 'The page is still blank after three reloads. Check the dev server.'}
            <Button variant="outline" size="xs" onClick={() => { setBlank(false); setBlocker(false); setTries(0); setLoading(true); link().then(setSrc, setErr) }}
              className="rounded-xs text-fg">Reload</Button>
          </div>
        </div>
      )}
      {loading && !err && (
        <div className="absolute inset-0 grid place-items-center bg-bg text-xs text-fg-subtle">
          <div className="flex flex-col items-center gap-3">
            <div className="size-5 animate-spin rounded-full border-2 border-border border-t-fg-subtle" />
            Loading the preview…
          </div>
        </div>
      )}
    </div>
  )
}

/** Only what needs attention: a lost connection, read-only, an eye with a count when others watch. */
function Status({ id }: { id: string }) {
  const s = useDock((x) => x.status[id])
  if (!s) return null
  return (
    <>
      {s.state !== 'open' && <span role="status" aria-label={s.state} title={s.state} className={cn('size-2 shrink-0 rounded-full', s.state === 'unreachable' || s.state === 'ended' ? 'bg-err' : 'bg-warn')} />}
      {s.readOnly && <span className="text-fg-faint">read-only</span>}
    </>
  )
}

/** Who the preview signs in as: the project file's dev.users. Picking another reloads the preview. */
function PreviewUser() {
  const { project } = useSession()
  const users = useProject(project).data?.settings.devUsers ?? []
  const as = useAs(project)
  const pick = usePreviewAs((x) => x.set)
  if (!users.length) return null
  const cur = users.find((u) => u.email === as) ?? users[0]!
  return (
    <Menu
      trigger={<Button variant="ghost" size="xs" title="Sign the preview in as" aria-label="Sign the preview in as"
        className="h-5 max-w-44 gap-1 rounded-xs px-1.5 font-normal text-fg-subtle hover:bg-hover has-[>svg]:px-1.5"><UserRound size={11} /><span className="truncate">{cur.label ?? cur.email}</span><span className="text-fg-faint">▾</span></Button>}
      items={users.map((u) => ({
        label: <span className="flex items-center gap-2">{u.email === cur.email ? <Check size={12} /> : <span className="w-3" />}<span>{u.label ?? u.email}</span>{u.label && <span className="text-fg-faint">{u.email}</span>}</span>,
        onSelect: () => { if (u.email !== cur.email) pick(project, u.email) },
      }))}
    />
  )
}

function PreviewMenu() {
  const { session } = useSession()
  const { start, stop, help } = useRun(session)
  const bump = useDock((s) => s.bump)
  const api = useDockApi()
  const term = useDock((s) => !!s.status.devserver) && !!api?.getPanel('devserver')
  const error = start.error ?? stop.error ?? help.error
  return (
    <>
      {error && <span className="max-w-48 truncate text-err" title={(error as Error).message}>{(error as Error).message}</span>}
      {help.isSuccess && <span className="text-ok">sent to Claude</span>}
      <Menu
        trigger={<Button variant="ghost" size="icon-xs" aria-label="Preview actions" title="Preview actions" className="rounded-xs text-fg-subtle hover:bg-hover"><MoreHorizontal size={14} /></Button>}
        items={[
          { label: 'Reload the page', onSelect: bump },
          { label: 'Open in a new tab', onSelect: () => void previewLink(session.id).then((u) => window.open(u, '_blank', 'noopener')) },
          { label: term ? 'Hide the dev server terminal' : 'Show the dev server terminal', onSelect: () => api && toggleDevServer(api) },
          'sep',
          // Restart replaces only the dev server's terminal; Claude's is never touched.
          { label: 'Restart the dev server', onSelect: () => start.mutate(undefined, { onSuccess: () => setTimeout(bump, 1500) }) },
          ...(!session.jobId ? [{ label: 'Ask Claude to fix it', onSelect: () => help.mutate() }] : []),
          { label: 'Stop the dev server', onSelect: () => stop.mutate(), danger: true },
        ]}
      />
    </>
  )
}

const DockApi = createContext<DockviewApi | null>(null)
const useDockApi = () => useContext(DockApi)

/** Opens a window that is not open yet; the preview starts the dev server first. */
function AddWindow({ group }: { group: IDockviewHeaderActionsProps['group'] }) {
  const { session, run, openRun } = useSession()
  const { start } = useRun(session)
  const api = useDockApi()
  const chat = useConversations((s) => (session ? s.bySession[session.id] : undefined))
  if (!api) return null
  const add = (id: Exclude<keyof typeof components, 'devserver'>) =>
    api.addPanel({ id, component: id, title: titles[id], position: { referenceGroup: group } })
  const items = ([
    ['claude', 'Chat'], ['terminal', 'Terminal'], ['files', 'Files'], ['preview', 'Preview'],
  ] as const).filter(([id]) => !api.getPanel(id)).map(([id, label]): { label: string; onSelect: () => void } => ({
    label,
    onSelect: () => id === 'preview' ? (run ? addMissing(api, true) : start.mutate(undefined, { onSuccess: openRun })) : void add(id),
  }))
  // A new conversation for the chat, beside the windows: one place to start things.
  if (chat?.canSwitch && api.getPanel('claude')) items.unshift({ label: 'New chat', onSelect: () => chat.switchTo() })
  if (!items.length) return null
  return <Menu trigger={<Button variant="ghost" size="icon-xs" aria-label="Open a window" title="Open a window" className="size-6 rounded-sm text-fg-subtle hover:bg-hover"><Plus size={14} /></Button>} items={items} />
}

/** The chat's conversation picker, on the tab row so it costs the chat no height. */
function ChatConversations({ session }: { session: string }) {
  const c = useConversations((s) => s.bySession[session])
  if (!c) return null
  return <ConversationBar compact hideNew items={c.items} answered={c.answered} canSwitch={c.canSwitch} onOpen={c.refresh} onSwitch={c.switchTo} />
}

function RightActions({ activePanel, group }: IDockviewHeaderActionsProps) {
  const { session, project, run, openRun } = useSession()
  const { start } = useRun(session)
  const id = activePanel?.id
  return (
    <div className="flex h-full items-center gap-1 px-2 text-xs [&>*]:shrink-0">
      {id === 'claude' && session && <ChatConversations session={session.id} />}
      {id === 'preview' ? <><PreviewUser /><PreviewMenu /></> : id && id !== 'files' ? <Status id={id} /> : null}
      {id === 'claude' && !run && (
        <RoleGate action="session.manage" project={project}>
          <Button variant="ghost" size="xs" title="Start the dev server and show it here" disabled={start.isPending}
            className="h-6 gap-1 rounded-sm bg-green-600 px-2 font-medium text-white hover:bg-green-500 hover:text-white disabled:opacity-50 has-[>svg]:px-2"
            onClick={() => start.mutate(undefined, { onSuccess: openRun })}><Play size={11} />{start.isPending ? 'Starting…' : 'Run'}</Button>
        </RoleGate>
      )}
      {id === 'claude' && start.error && <span className="max-w-48 truncate text-err">{(start.error as Error).message}</span>}
      {(id === 'claude' || id === 'terminal') && <McpButton project={project} sessionId={session?.id} />}
      <AddWindow group={group} />
    </div>
  )
}

const components = { claude: ClaudeWindow, terminal: TerminalWindow, files: FilesWindow, preview: PreviewWindow, devserver: DevServerWindow }
const titles: Record<string, string> = { claude: 'Chat', terminal: 'Terminal', files: 'Files', preview: 'Preview', devserver: 'Dev server' }

/** The dev server's terminal is opened from the preview's menu, below it. */
function toggleDevServer(api: DockviewApi) {
  const p = api.getPanel('devserver')
  if (p) p.api.close()
  else api.addPanel({ id: 'devserver', component: 'devserver', title: titles.devserver, position: { referencePanel: 'preview', direction: 'below' }, initialHeight: 220 })
}

/** Never an empty dock (it would have no header, so no +); the preview goes right of whatever is open. */
function addMissing(api: DockviewApi, run: boolean) {
  if (!api.panels.length) api.addPanel({ id: 'claude', component: 'claude', title: titles.claude })
  if (!run || api.getPanel('preview')) return
  const ref = api.getPanel('claude') ?? api.panels[0]!
  api.addPanel({ id: 'preview', component: 'preview', title: titles.preview, position: { referencePanel: ref.id, direction: 'right' } })
}

export function SessionDock({ session, project, run, onRunClosed, onRunOpen }: { session: AgentSession; project: string; run: boolean; onRunClosed: () => void; onRunOpen: () => void }) {
  const api = useRef<DockviewApi | null>(null)
  const [dock, setDock] = useState<DockviewApi | null>(null)
  const closed = useRef(onRunClosed)
  closed.current = onRunClosed

  const onReady = (e: DockviewReadyEvent) => {
    api.current = e.api
    setDock(e.api)
    try {
      const saved = localStorage.getItem(LAYOUT_KEY)
      if (saved && run) e.api.fromJSON(JSON.parse(saved))
    } catch { e.api.clear() }
    addMissing(e.api, run)
    e.api.onDidLayoutChange(() => {
      // Remember the arrangement only while the run is open, so closing it
      // does not forget where its windows were.
      if (e.api.getPanel('preview')) localStorage.setItem(LAYOUT_KEY, JSON.stringify(e.api.toJSON()))
    })
    e.api.onDidRemovePanel((p) => {
      queueMicrotask(() => addMissing(e.api, false))
      // The preview is the run: closing it closes its terminal too.
      if (p.id === 'preview') { e.api.getPanel('devserver')?.api.close(); closed.current() }
    })
  }

  useEffect(() => {
    const a = api.current
    if (!a) return
    if (run && !a.getPanel('preview')) {
      const saved = localStorage.getItem(LAYOUT_KEY)
      if (saved) { try { a.fromJSON(JSON.parse(saved)) } catch { /* fall through */ } }
      addMissing(a, true)
    }
    if (!run) for (const id of RUN_PANELS) a.getPanel(id)?.api.close()
  }, [run])

  return (
    <SessionCtx.Provider value={{ session, project, run, openRun: onRunOpen }}>
      <DockApi.Provider value={dock}>
        <DockviewReact key={session.id} className="trisof-dock h-full" theme={themeAbyss} components={components} onReady={onReady}
          rightHeaderActionsComponent={RightActions} />
      </DockApi.Provider>
    </SessionCtx.Provider>
  )
}
